import { Box, Stack, Typography, Theme, useTheme } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { useTranslation } from 'react-i18next';

import { ChargingBarProps } from './types';
import {
  clampPercent,
  deriveTargetKwh,
  elapsedMinutes,
  estimateRemainingMinutes,
  formatDuration,
  formatKw,
  formatKwh,
} from './chargingMetrics';
import { ProgressBar } from '../../../../components/common/ProgressBar/ProgressBar';

const Metric = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.75}
    sx={{ minWidth: 0 }}
  >
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        color: theme.palette.green.main,
        '& svg': { fontSize: 18 },
      })}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={(theme: Theme) => ({
          fontSize: 11,
          lineHeight: 1.2,
          color: theme.palette.gray[100],
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        })}
      >
        {label}
      </Typography>
      <Typography
        noWrap
        sx={{ fontSize: 14, fontWeight: 600, color: 'common.white', lineHeight: 1.3 }}
      >
        {value}
      </Typography>
    </Box>
  </Stack>
);

export const ChargingBar: React.FC<ChargingBarProps> = ({
  percent,
  chargedKwh,
  powerKw,
  startedAt,
  complete = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const safePercent = clampPercent(percent);
  const target = deriveTargetKwh(chargedKwh, percent);
  const remainingKwh =
    target != null && chargedKwh != null ? Math.max(0, target - chargedKwh) : null;
  const remaining = estimateRemainingMinutes(remainingKwh, powerKw);
  const elapsed = elapsedMinutes(startedAt);

  const calculating = t('orderPage.calculating');
  const powerText = formatKw(powerKw) ?? calculating;
  const chargedLabel = formatKwh(chargedKwh);
  const targetLabel = target != null ? formatKwh(target) : null;
  const energyText =
    targetLabel != null
      ? `${chargedLabel} / ${targetLabel} kWh`
      : `${chargedLabel} kWh`;

  // In the completed summary, derive the percentage from the same rounded
  // energy figures shown to the user, so "0.9 / 1.0 kWh" reads as 90% rather
  // than a slightly different value from independent backend rounding.
  const displayPercent =
    complete && targetLabel != null && Number(targetLabel) > 0
      ? clampPercent((Number(chargedLabel) / Number(targetLabel)) * 100)
      : safePercent;
  const remainingText = formatDuration(remaining) ?? calculating;
  const elapsedText = formatDuration(elapsed) ?? '—';

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: complete ? theme.palette.green.main : theme.palette.gray[100],
          }}
        >
          {complete ? t('orderPage.completed') : t('orderPage.charging')}
        </Typography>
        <Typography
          sx={{ fontSize: 32, fontWeight: 700, color: 'common.white', lineHeight: 1 }}
        >
          {Math.round(displayPercent)}%
        </Typography>
      </Stack>

      <ProgressBar
        percent={displayPercent}
        height={12}
        fillBackground={`linear-gradient(90deg, ${theme.palette.green.main}99, ${theme.palette.green.main})`}
        glowColor={theme.palette.green.main}
        shimmer={!complete}
      />

      <Box
        sx={{
          mt: 2,
          display: 'grid',
          // Active charging shows four metrics as a 2×2 grid (power + energy on
          // the first row, time left + elapsed on the second); the completed
          // summary has a single metric, so one column.
          gridTemplateColumns: complete ? '1fr' : '1fr 1fr',
          columnGap: 1.5,
          rowGap: 1.5,
        }}
      >
        {complete ? (
          <Metric
            icon={<BatteryChargingFullRoundedIcon />}
            label={t('orderPage.energy')}
            value={energyText}
          />
        ) : (
          <>
            <Metric
              icon={<BoltRoundedIcon />}
              label={t('orderPage.power')}
              value={powerText}
            />
            <Metric
              icon={<BatteryChargingFullRoundedIcon />}
              label={t('orderPage.energy')}
              value={energyText}
            />
            <Metric
              icon={<HourglassBottomRoundedIcon />}
              label={t('orderPage.timeRemaining')}
              value={remainingText}
            />
            <Metric
              icon={<TimerOutlinedIcon />}
              label={t('orderPage.timeElapsed')}
              value={elapsedText}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
